<template>
    <div class="box">
        <!-- Loading. -->
        <img class="logo-loader" v-if="isLoading || !ready" src="@/assets/images/logo-active-progress.png" alt />
        <div class="lds-roller" v-if="!ready || isLoading">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
        </div>
        <transition name="fade">
            <div class="dark-wall" v-if="isLoading" />
        </transition>
        <div class="home" v-if="ready" :class="{ par0i: isMenu }">
            <div class="home-map col-12" :class="[isMenu ? 'col-md-8 col-lg-9' : 'col-md-12']">
                <!-- Map. -->
                <tv-map @ready="(map = $event), $connect()" @gpx:ready="gpxReady = true"
                    :classifications="competitionClassifications" class="map" ref="map">
                    <!-- Device markers -->
                    <base-map-marker v-for="player in listPlayers" :key="player.id"
                        @ready="onReadyMarker($event, player)" :device="player._device()" :latLng="[
                            positions[player.deviceId].latitude,
                            positions[player.deviceId].longitude,
                        ]" :visible="player.isVisible && player.isLocatedInTrackZone
                            ">
                        <template #icon>
                            <!-- players[id].deviceId -->
                            <div v-if="player.playerNumber" class="player-number">
                                {{ player.playerNumber }}
                            </div>
                            <IconOval :deviceId="player.deviceId" :iconActive="hasSelectedClassification(player)" />
                        </template>
                        <template #popup>
                            {{
                                player.id
                                    ? `${player.firstname} ${player.lastname}`
                                    : player.specialName
                            }}
                        </template>
                    </base-map-marker>
                    <!-- Points.  -->
                    <map-point v-for="point in points" :key="point.id" :classifications="competitionClassifications"
                        :point="point" />
                </tv-map>
                <!-- Organizer/operator logo. -->
                <div class="home-map__logo">
                    <img :src="logoLink" alt="Logo" />
                </div>
                <transition name="slide">
                    <!-- Menu icon. -->
                    <!-- <div class="home-map__menu-btn" @click="isMenu = !isMenu">
                        <i class="fas fa-address-book fa-flip-horizontal" />
                        <i class="fa fa-share fa-flip-vertical" :class="isMenu ? '' : 'fa-flip-horizontal'" />
                        <i :class="isMenu ? 'fas fa-times' : 'fas fa-info-circle'" />
                    </div> -->
                </transition>
                <template v-if="isPlaybackReady">
                    <base-controls :playback="$options.playback" />
                </template>
            </div>
            <transition name="slide">
                <!-- Devices menu. -->
                <tv-devices-menu v-if="map" class="home-map__menu col-12 col-md-4 col-lg-3" :isMenu.sync="isMenu"
                    :classifications="competitionClassifications" :players="listPlayers" v-show="isMenu" />
            </transition>
        </div>
    </div>
</template>

<script>
import TvDevicesMenu from '@/components/tView/TvDevicesMenu'
import TvMap from '@/components/tView/TvMap'
import BaseMapMarker from '@/components/BaseMapMarker'
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { SET_MARKER, SET_PLAYBACK_MARKERS } from '@/store/mutations.type'
import { TRACKPOINT_TYPES } from '@/config'
import { createPoints } from '@/service/map'
import { BOOTSTRAP_MAP } from '@/store/actions.type'
import { SET_SELECTED_CLASSIFICATION_ID } from '@/store/mutations.type'
import MapPoint from '@/components/MapPoint'
import _ from 'lodash'
import {
    SET_ENTITIES,
    CLEAR_ENTITIES,
    SET_IS_PLAYBACK_READY,
    SET_FOCUS_DEVICE_ID,
} from '@/store/mutations.type'
import schema from '@/api/schema'
import { normalize } from 'normalizr'
import BaseControls from '@/components/BaseControls'
import IconOval from '@/components/IconOval'
import { initPlayback } from '@/service/playback'
import moment from 'moment'
export default {
    components: {
        TvDevicesMenu,
        TvMap,
        BaseMapMarker,
        MapPoint,
        BaseControls,
        IconOval,
    },
    props: {
        id: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            isMenu: true,
            types: TRACKPOINT_TYPES,
            ready: false,
            map: {},
            prevSelectedClassificationId: null,
            gpxReady: false,
            isPlay: false,
            playback: undefined,
            isLoading: false,
        }
    },
    computed: {
        ...mapGetters([
            'devices',
            'awardedDeviceIds',
            'isHighlightedDevicesView',
            'focusDeviceId',
            'trackpointsBy',
            'classifications',
            'selectedClassificationId',
            'markers',
            'players',
            'tracksBounds',
            'positions',
            'isLiveMode',
            'isPlaybackReady',
            'competitions',
            'playbackMarkers',
            'competitionLayouts',
            'isShowAllNames',
        ]),
        listPlayers() {
            // isVisible defines when to show the player
            let players = Object.values(this.players)
            const playersWithDevice = players.filter(player => player.deviceId)
            for (let player of playersWithDevice) {
                if (this.isHighlightedDevicesView) {
                    player.isVisible = true
                } else {
                    player.isVisible =
                        this.hasSelectedClassification(player) ||
                        this.isSpecial(player)
                }
                player.isLocatedInTrackZone = this.isLocatedInTrackBoundsZone(player)
            }
            return playersWithDevice
        },
        logoLink() {
            return this.competitionLayouts[
                this.competition.id
            ].logoLink.replace('zawodybiegowe', 'dostartu')
        },
        focusedDevicePosition() {
            return this.devices[this.focusDeviceId]?._position()
        },
        competition() {
            return this.competitions[this.id]
        },
        competitionClassifications() {
            return this.competition._classifications()
        },
        trackpoints() {
            return this.trackpointsBy(this.competitionClassifications)
        },
        points() {
            return createPoints(this.trackpoints)
        },
    },
    async created() {
        // Bootstrap map.
        await this.BOOTSTRAP_MAP(this.id)
        // Adding websocket listener.
        this.$options.sockets.onmessage = ({ data }) => {
            let parseData = JSON.parse(data)
            if (parseData.positions) {
                // Update positions entity.
                const normalized = normalize(parseData.positions, [
                    schema.position,
                ])
                this.SET_ENTITIES(normalized.entities)
            }
        }
        if (window.innerWidth < 768) {
            this.isMenu = false
        }
        this.ready = true
    },

    methods: {
        ...mapMutations([
            SET_SELECTED_CLASSIFICATION_ID,
            SET_MARKER,
            SET_ENTITIES,
            CLEAR_ENTITIES,
            SET_IS_PLAYBACK_READY,
            SET_PLAYBACK_MARKERS,
            SET_FOCUS_DEVICE_ID,
        ]),
        ...mapActions([
            BOOTSTRAP_MAP
        ]),

        hasSelectedClassification(player) {
            return player.classification === this.selectedClassificationId
        },
        isSpecial(player) {
            return Boolean(player.specialName)
        },
        isLocatedInTrackBoundsZone(player) {
            if (
                !this.isPlaybackReady &&
                this.gpxReady &&
                this.tracksBounds[player.classification] &&
                this.positions[player.deviceId]
            ) {
                return this.tracksBounds[
                    player.classification
                ].contains([
                    this.positions[player.deviceId].latitude,
                    this.positions[player.deviceId].longitude,
                ])
            }
            if (this.isPlaybackReady && this.playbackMarkers[player.deviceId]) {
                return this.tracksBounds[
                    this.selectedClassificationId
                ].contains(this.playbackMarkers[player.deviceId].getLatLng())
            }
            return false
        },
        getEndTime() {
            let startTime
            let currentClassification = this.classifications[
                this.selectedClassificationId
            ]
            let endTime = currentClassification.endTime

            if (currentClassification.startTime) {
                startTime = currentClassification.startTime.clone()
            } else {
                startTime = this.competition.startedTime.clone()
            }
            // when the classification ended
            if (endTime) {
                return endTime.clone()
            } else if (!endTime && moment().diff(startTime) > 1209600000) {
                // 1 209 600 000ms = 14 days.

                return startTime.add(1209600000, 'ms')
            } else {
                return moment()
            }
        },
        // Setup marker's handlers (Live mode)
        onReadyMarker(marker, player) {
            marker.deviceId = player.deviceId
            marker.trackNumber = player.trackNumber
            if (player.classification) {
                marker.on('mouseover', event => {
                    event.target.openPopup()
                    if (
                        player.classification !== this.selectedClassificationId
                    ) {
                        this.prevSelectedClassificationId = this.selectedClassificationId
                        this.SET_SELECTED_CLASSIFICATION_ID(
                            player.classification
                        )
                    }
                })
                // Change classification on marker click.
                marker.on('click', () => {
                    if (player.classification) {
                        this.SET_SELECTED_CLASSIFICATION_ID(
                            player.classification
                        )
                        this.prevSelectedClassificationId = null
                    }
                })
                marker.on('mouseout', event => {
                    if (
                        event.target.deviceId !== this.focusDeviceId &&
                        !this.isShowAllNames
                    ) {
                        event.target.closePopup()
                    }
                    if (this.prevSelectedClassificationId) {
                        this.SET_SELECTED_CLASSIFICATION_ID(
                            this.prevSelectedClassificationId
                        )
                        this.prevSelectedClassificationId = null
                    }
                })
                marker.on('popupclose', event => {
                    if (this.isShowAllNames) {
                        setTimeout(() => event.target.openPopup(), 1000)
                    }
                })
            }
            this.SET_MARKER(marker)
        },
        setCenterOnMap(devicePosition) {
            if (devicePosition) {
                this.map.setView(
                    [devicePosition.latitude, devicePosition.longitude],
                    17
                )
            } else {
                // Fit to track if unfocused device.
                let bounds
                this.map.eachLayer(layer => {
                    if (
                        layer._gpx &&
                        layer.classification === this.selectedClassificationId
                    ) {
                        bounds = layer.getBounds()
                    }
                })
                this.map.fitBounds(bounds)
            }
        },
        setPopup(devicePosition, oldDevicePosition) {
            let deviceId = devicePosition?.deviceId
            let oldDeviceId = oldDevicePosition?.deviceId
            let marker
            let oldMarker
            if (deviceId) {
                // Live mode
                if (this.isLiveMode) {
                    marker = _.find(
                        this.markers,
                        marker => marker.deviceId === deviceId
                    )
                } else {
                    // Playback mode
                    marker = _.find(
                        this.playbackMarkers,
                        marker => marker.feature.properties.device === deviceId
                    )
                }

                marker.off('mouseout')
                marker.openPopup()
                marker.openTooltip()
            }
            // Set on 'mouseout' in old focusedDevice.
            if (oldDeviceId && oldDeviceId !== deviceId) {
                // Live mode
                if (this.isLiveMode) {
                    oldMarker = _.find(
                        this.markers,
                        marker => marker.deviceId === oldDeviceId
                    )
                } else {
                    // Playback mode
                    oldMarker = _.find(
                        this.playbackMarkers,
                        marker =>
                            marker.feature.properties.device === oldDeviceId
                    )
                }
                if (oldMarker) {
                    oldMarker.closePopup()
                    oldMarker.closeTooltip()
                    oldMarker.on('mouseout', event => {
                        if (oldDeviceId !== this.focusDeviceId) {
                            event.target.closePopup()
                            event.target.closeTooltip()
                        }
                    })
                }
            }
        },
    },
    watch: {
        // FocusedDevice changed on focusedDevicePosition (socket doesnt listen "devices" messages(useless) so need watching devices's positions)
        //@TODO Check for playback mode, live mode working :)
        focusedDevicePosition: {
            deep: true,
            handler(devicePosition, oldDevicePosition) {
                if (this.map._loaded) {
                    this.setPopup(devicePosition, oldDevicePosition)
                }
            },
        },
        isMenu() {
            // Timeout for waiting on menu transition.
            setTimeout(() => this.map.invalidateSize(true), 1000)
        },
        isLiveMode() {
            if (this.focusDeviceId) {
                this.SET_FOCUS_DEVICE_ID(null)
            }
            this.isLoading = true
            // Timeout for transition

            if (this.isLiveMode && this.isPlaybackReady) {
                this.$connect()
                setTimeout(() => {
                    this.$options.playback.clearData()
                    this.SET_IS_PLAYBACK_READY(false)
                    this.SET_PLAYBACK_MARKERS(null)
                    this.isLoading = false
                }, 400)
            }

            if (!this.isLiveMode) {
                this.$disconnect()
                // Timeout for transition
                setTimeout(async () => {
                    let fromTime = this.competition.startedTime.clone()
                    fromTime = fromTime.toISOString()

                    let endTime = this.getEndTime()
                    let toTime = endTime.toISOString()
                    let { playback, markers } = await initPlayback(
                        this.map,
                        fromTime,
                        toTime
                    )
                    this.$options.playback = playback
                    this.SET_PLAYBACK_MARKERS(markers)
                    this.SET_IS_PLAYBACK_READY(true)
                    this.isLoading = false
                }, 400)
            }
        },
    },
}
</script>
<style lang="scss" scoped>
.slide-enter,
.slide-leave-to {
    opacity: 0;
    transform: translateX(100%);
}

.slide-enter-active {
    transition: all 0.4s ease;
}

.slide-leave-active {
    transition: all 0.4s ease;
}

@media (min-width: 768px) {
    .slide-enter-active {
        transition: all 0.8s ease;
    }

    .slide-leave-active {
        transition: all 0.4s ease;
    }

    .slide-enter,
    .slide-leave-to {
        transform: translateX(100%);
        opacity: 0;
    }
}

.map {
    height: 100%;
    width: 100%;
}

.home {
    display: flex;
    overflow: hidden;
    height: 100%;

    &-map {
        height: 100%;
        position: relative;
        text-align: center;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        background-color: $white;
        padding-left: 0;
        transition: all 0.8s;
        padding-right: 0;

        @media (max-width: 767px) {
            &--hide-mobile {
                display: none;
            }

            &__menu {
                position: absolute;
                z-index: 9999;
                background-color: $white;
            }
        }

        &__logo {
            position: absolute;
            z-index: 9999;
            top: 24px;
            left: 24px;
            height: 80px;
            width: 80px;
            max-height: 80px;
            max-width: 80px;
            border-radius: 4px;
            overflow: hidden;
            background-color: $white;
            padding: 8px;
            display: flex;

            // cursor: none;
            & img {
                width: 100%;
                object-fit: contain;
            }
        }

        @media (min-width: 768px) {
            &__logo {
                height: 128px;
                width: 128px;
                max-height: 128px;
                max-width: 128px;
            }
        }

        &__menu-btn {
            position: absolute;
            top: 8px;
            right: 0px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            z-index: 9999;
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
            width: 40px;
            font-size: 28px;
            background-color: $white;
            cursor: pointer;

            i {
                margin: 5px;
                transition: transform 1s;
            }
        }
    }

    @media (min-width: 768px) {
        padding: 16px;
    }
}

.box {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
}

.logo-loader {
    width: 140px;
    position: absolute;
    left: calc(50% - 70px);
    top: calc(45% - 70px);
    z-index: 999999;
}

// Loading map animation.
.lds-roller {
    display: inline-block;
    min-width: 80px;
    min-height: 80px;
    margin: auto;
    position: absolute;
    z-index: 999999;
    top: calc(50% - 40px);
    left: calc(50% - 40px);

    div {
        animation: lds-roller 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
        transform-origin: 40px 40px;

        &:after {
            content: ' ';
            display: block;
            position: absolute;
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: $charcoal-grey;
            margin: -4px 0 0 -4px;
        }

        &:nth-child(1) {
            animation-delay: -0.036s;

            &:after {
                top: 63px;
                left: 63px;
            }
        }

        &:nth-child(2) {
            animation-delay: -0.072s;

            &:after {
                top: 68px;
                left: 56px;
            }
        }

        &:nth-child(3) {
            animation-delay: -0.108s;

            &:after {
                top: 71px;
                left: 48px;
            }
        }

        &:nth-child(4) {
            animation-delay: -0.144s;

            &:after {
                top: 72px;
                left: 40px;
            }
        }

        &:nth-child(5) {
            animation-delay: -0.18s;

            &:after {
                top: 71px;
                left: 32px;
            }
        }

        &:nth-child(6) {
            animation-delay: -0.216s;

            &:after {
                top: 68px;
                left: 24px;
            }
        }

        &:nth-child(7) {
            animation-delay: -0.252s;

            &:after {
                top: 63px;
                left: 17px;
            }
        }

        &:nth-child(8) {
            animation-delay: -0.288s;

            &:after {
                top: 56px;
                left: 12px;
            }
        }
    }
}

.dark-wall {
    background-color: $charcoal-grey;
    opacity: 0.3;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    position: absolute;
    z-index: 99999;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}

@keyframes lds-roller {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.player-number {
    position: absolute;
    transform: translate(-45%, -80%);
    font-size: 18px;
    left: 50%;
    top: 0;
    padding: 6px;
    text-shadow: 2px 0 0 #fff, -2px 0 0 #fff, 0 2px 0 #fff, 0 -2px 0 #fff,
        1px 1px #fff, -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff;
    width: 40px;
    height: 40px;
    font-weight: bold;
}
</style>
